import { useMutation, useQuery } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { enqueueSnackbar } from 'notistack';

import axios from '../axios';

type TSendPasswordResetPayload = {
  email: string;
  redirectTo?: string;
};

export const useSendPasswordResetMutation = () => {
  return useMutation({
    mutationFn: async ({ email, redirectTo }: TSendPasswordResetPayload) => {
      const redirectUrl = redirectTo || `${window.location.origin}/auth/reset-password`;
      return axios.post('/auth/send-password-reset', { email, redirectUrl });
    },
    meta: {
      successMessage: 'Password reset email sent',
    },
    onError: (error: Error) => {
      const message =
        error instanceof AxiosError && error.response?.data?.message
          ? error.response.data.message
          : 'Failed to send password reset email';
      enqueueSnackbar(message, {
        variant: 'error',
        autoHideDuration: 3000,
        anchorOrigin: { vertical: 'bottom', horizontal: 'center' },
      });
    },
  });
};

export const getValidateResetTokenQueryKeys = (token: string) => ['validate-reset-token', token];

export const useValidateResetTokenQuery = (token: string) => {
  return useQuery({
    queryKey: getValidateResetTokenQueryKeys(token),
    queryFn: async () => {
      const { data } = await axios.post<{ valid: boolean }>('/auth/validate-reset-token', { token });
      return data;
    },
    enabled: !!token,
    retry: false,
    staleTime: Infinity,
  });
};

type TSetPasswordWithTokenPayload = {
  token: string;
  password: string;
};

export const useSetPasswordWithTokenMutation = (token: string) => {
  return useMutation({
    mutationFn: async ({ token, password }: TSetPasswordWithTokenPayload) => {
      return axios.post<{ email: string }>('/auth/set-password', { token, password });
    },
    meta: {
      errorMessage: 'Failed to reset password',
      invalidatesQuery: getValidateResetTokenQueryKeys(token),
    },
  });
};
